/**
 * The plan outline (R6, R54; board O „Zarys planu", board M's flow): what Buddy intends to build,
 * approved with a button before anything is built.
 *
 * A sentence streams in above the card (the wire carries none, so it is the app's own). The card
 * lists the outline's phases, a deload with the hatched marker; past three lines it shows three and a „Pokaż wszystkie" that unfolds the
 * rest in place. „Anuluj" leaves the conversation open, so the trainer can ask for a different
 * outline in their own words; only the newest turn's outline keeps its foot.
 */

import clsx from 'clsx';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AiPlanOutline } from '~/entities/ai-conversation';
import { ChevronDownGlyph } from '~/shared/ui/FormGlyphs';

import { FACT_SEPARATOR, schedulingWords } from '../lib/planWording';

import { Assumptions } from './Assumptions';
import { OutlineGlyph } from './ChatGlyphs';
import { StreamedText } from './Messages';
import surfaces from './surfaces.module.css';
import styles from './OutlineCard.module.css';

/** Rows shown folded (canon O): an outline with more shows these and the unfold, which counts rows. */
const FOLDED_LINES = 3;

interface OutlineCardProps {
  readonly outline: AiPlanOutline;
  /** The foot's two answers are offered: the newest turn, not cancelled. */
  readonly actionable: boolean;
  readonly onCancel: () => void;
  readonly onBuild: () => void;
  /** The card's own box, for „Wróć do zarysu" to bring back into view. */
  readonly cardRef?: (node: HTMLDivElement | null) => void;
}

export function OutlineCard({ outline, actionable, onCancel, onBuild, cardRef }: OutlineCardProps) {
  const { t } = useTranslation();
  const [unfolded, setUnfolded] = useState(false);
  const titleId = useId();
  const folds = outline.phases.length > FOLDED_LINES;
  const lines = folds && !unfolded ? outline.phases.slice(0, FOLDED_LINES) : outline.phases;

  const facts = [
    t('web:aiPlan.sessions', { count: outline.daysPerWeek }),
    t('web:aiPlan.cycles', { count: outline.cycleCount }),
    ...schedulingWords(t, outline.schedulingMode, outline.startWeekday),
  ];

  return (
    <>
      <StreamedText text={t('web:aiPlan.outline.intro')} />
      <div
        ref={cardRef}
        tabIndex={-1}
        role="region"
        aria-labelledby={titleId}
        className={clsx(surfaces.card, surfaces.riseCard, styles.card)}
      >
        <p className={styles.eyebrow}>
          <OutlineGlyph className={styles.eyebrowGlyph} />
          {t('web:aiPlan.outline.eyebrow')}
        </p>
        <h3 id={titleId} className={styles.title}>
          {outline.title}
        </h3>
        <p className={styles.meta}>{facts.join(FACT_SEPARATOR)}</p>
        <ul className={styles.cycles}>
          {lines.map((phase, index) => (
            <li key={index} className={styles.cycle}>
              <span className={clsx(styles.bullet, phase.lightweight && styles.bulletLight)} aria-hidden="true" />
              <span className={styles.cycleTitle}>{phase.name}</span>
              <span className={styles.cycleSpan}>{t('web:aiPlan.cycles', { count: phase.cycleCount })}</span>
              <span className={styles.cycleDetail}>{phase.line}</span>
            </li>
          ))}
        </ul>
        {folds && (
          <div className={styles.more}>
            <button
              type="button"
              className={styles.moreButton}
              aria-expanded={unfolded}
              onClick={() => {
                setUnfolded((current) => !current);
              }}
            >
              {unfolded
                ? t('web:aiPlan.outline.fold')
                : t('web:aiPlan.outline.unfold', { count: outline.phases.length })}
              <ChevronDownGlyph className={clsx(styles.moreGlyph, unfolded && styles.moreGlyphUp)} />
            </button>
          </div>
        )}
        {outline.assumptions.length > 0 && (
          <div className={styles.assumptions}>
            <Assumptions items={outline.assumptions} />
          </div>
        )}
        {actionable && (
          <div className={styles.foot}>
            <span className={styles.hint}>{t('web:aiPlan.outline.hint')}</span>
            <button type="button" className={clsx(surfaces.button, surfaces.ghost)} onClick={onCancel}>
              {t('web:aiPlan.outline.cancel')}
            </button>
            <button type="button" className={clsx(surfaces.button, surfaces.primary)} onClick={onBuild}>
              {t('web:aiPlan.outline.build')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
